import { createContext, useContext, ReactNode, useMemo } from "react";
import { useFieldArray, UseFieldArrayReturn, useFormContext } from "react-hook-form";

import useAlert from "@rf-hooks/useAlert";
import {
  BuildingUnitSchema,
  CPropertyBuildingUnitForm,
  IPropertyBuildingUnitForm
} from "../../../../util/schemas/building-form/building-unit.schema";
import { CPropertyFormKeys } from "../../../../util/schemas/property-schema/propertySchema";
import { IPropertyBuildingForm } from "../../../../util/schemas/building-form/building.schema";

interface UnitsContextType {
  addUnit: () => void;
  deleteUnit: (index: number) => void;
  duplicateUnit: (index: number, times?: number) => void;
  fields: (IPropertyBuildingUnitForm & { uuid: string; id: string })[];
  append: UseFieldArrayReturn["append"];
  remove: UseFieldArrayReturn["remove"];
  update: UseFieldArrayReturn["update"];
}

const UnitsContext = createContext<UnitsContextType | undefined>(undefined);

interface UnitsProviderProps {
  children: ReactNode;
  buildingIndex: number;
}

export const UnitsProvider = ({ children, buildingIndex }: UnitsProviderProps) => {
  const { addUnit, deleteUnit, duplicateUnit, fields, append, remove, update } =
    useUnitsHelp(buildingIndex);

  const unitFields = useMemo(() => {
    return fields as (IPropertyBuildingUnitForm & { uuid: string; id: string })[];
  }, [fields.length]);

  const memoizedValue = useMemo(() => {
    return {
      addUnit,
      deleteUnit,
      duplicateUnit,
      fields: unitFields,
      append,
      remove,
      update
    };
  }, [unitFields, buildingIndex]);

  return (
    <UnitsContext.Provider value={memoizedValue as any}>
      {children}
    </UnitsContext.Provider>
  );
};

const useUnitsHelp = (buildingIndex: number) => {
  const { control, getValues, setValue } = useFormContext();
  const { alert } = useAlert();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: `${CPropertyFormKeys.building}.${buildingIndex}.units`
  });

  const getNewUnit = () => {
    const formValues = getValues();
    const building = formValues[CPropertyFormKeys.building][buildingIndex];

    return {
      ...CPropertyBuildingUnitForm,
      id: crypto.randomUUID(),
      uuid: crypto.randomUUID(),
      isNew: true
    };
  };

  const addUnit = () => {
    append(getNewUnit());
  };

  const deleteUnit = (index: number) => {
    const buildings = getValues(CPropertyFormKeys.building) as
      | IPropertyBuildingForm[]
      | undefined;
    if (!Array.isArray(buildings) || !buildings[buildingIndex]) {
      // Inconsistencia inesperada: el índice del edificio ya no existe (pudo cambiar tras borrar otro edificio)
      console.warn(
        "deleteUnit: edificio no encontrado para el índice",
        buildingIndex
      );
      return;
    }

    const building = buildings[buildingIndex];

    const buildingUnits = building.units || [];
    const buildingCaptured = building.isCaptured;
    const buildingsCaptured = buildings.filter(
      (b: IPropertyBuildingForm) => b.isCaptured
    ).length;

    // Reglas de validación
    if (buildingUnits.length === 1 && buildingCaptured) {
      alert({
        title: "No se puede eliminar la unidad",
        type: "warning",
        message: "No se puede eliminar la última unidad de un edificio capturado.",
        color: "warning.main",
        confirmBtnMessage: "Entendido"
      });
      return;
    }

    if (buildingUnits.length === 1 && !buildingCaptured && buildingsCaptured === 0) {
      alert({
        title: "No se puede eliminar la unidad",
        type: "warning",
        message:
          "Debe existir al menos una unidad en el edificio cuando no hay otros edificios capturados.",
        color: "warning.main",
        confirmBtnMessage: "Entendido"
      });
      return;
    }

    const currentUnitsDeleted = getValues(`${CPropertyFormKeys.unitsDeleted}`) || [];

    // Si la unidad es nueva, solo la removemos del formulario
    if (buildingUnits[index]?.isNew) {
      remove(index);
      return;
    }

    // Si la unidad no es nueva, la registramos como eliminada y la removemos
    if (buildingUnits[index]) {
      setValue(`${CPropertyFormKeys.unitsDeleted}`, [
        ...currentUnitsDeleted,
        buildingUnits[index].id
      ]);
    }

    remove(index);
  };

  const duplicateUnit = async (index: number, times = 1) => {
    if (times > 10) {
      alert({
        title: "Límite excedido",
        type: "warning",
        message: "El máximo de unidades a duplicar es 10",
        color: "warning.main",
        confirmBtnMessage: "Entendido"
      });
      return;
    }

    const unitToDuplicate = getValues(
      `${CPropertyFormKeys.building}[${buildingIndex}].units[${index}]`
    ) as IPropertyBuildingUnitForm & {
      uuid: string;
      id: string;
    };

    try {
      await BuildingUnitSchema.validate(unitToDuplicate, {
        abortEarly: false
      });

      for (let i = 0; i < times; i++) {
        const duplicatedUnit = {
          ...unitToDuplicate,
          id: crypto.randomUUID(),
          uuid: crypto.randomUUID(),
          isNew: true,
          name: `${unitToDuplicate.name} `
        };
        append(duplicatedUnit);
      }
    } catch (error) {
      alert({
        title: "Atención",
        type: "warning",
        message:
          "Complete los campos requeridos de la unidad, para poder duplicarla.",
        color: "warning.main",
        confirmBtnMessage: "Entendido"
      });
      return;
    }
  };

  return {
    addUnit,
    deleteUnit,
    duplicateUnit,
    fields,
    append,
    remove,
    update
  };
};

export const useUnitsContext = () => {
  const context = useContext(UnitsContext);
  if (context === undefined) {
    throw new Error("useUnitsContext must be used within a UnitsProvider");
  }
  return context;
};
